import React, { useRef, useState } from 'react';

// Insight entries live one-per-line inside the section's TEXT column, so a legacy
// single-paragraph value reads back as a one-item list with no backfill.
export const splitItems = (text) => String(text || '').split('\n').map(s => s.trim()).filter(Boolean);
export const joinItems = (items) => items.join('\n');

// Newline is the separator, so an entry can never hold one — pasted breaks collapse to spaces.
const oneLine = (s) => String(s ?? '').replace(/\s*\n+\s*/g, ' ').trim();

/**
 * One insight category (R&R, Market Insights, Challenges) as an add/edit/delete list.
 * The parent owns the stored text; this asks it to save the whole joined value.
 */
function InsightList({ label, text, editable, onSave, emptyLabel }) {
  const items = splitItems(text);
  const [editIdx, setEditIdx] = useState(null);   // item index | 'new' | null
  const [val, setVal] = useState('');
  const skipBlur = useRef(false);                 // Escape unmounts the box; don't let blur commit

  const close = () => { setEditIdx(null); setVal(''); };
  const open = (idx, initial) => {
    if (!editable) return;
    skipBlur.current = false;
    setEditIdx(idx);
    setVal(initial);
  };

  const commit = () => {
    const v = oneLine(val);
    if (editIdx === 'new') {
      if (v) onSave(joinItems([...items, v]));
    } else if (editIdx != null) {
      const next = [...items];
      if (v) next[editIdx] = v; else next.splice(editIdx, 1);
      if (joinItems(next) !== joinItems(items)) onSave(joinItems(next));
    }
    close();
  };

  const cancel = () => { skipBlur.current = true; close(); };
  const remove = (idx) => onSave(joinItems(items.filter((_, i) => i !== idx)));

  const editor = (
    <textarea
      className="md-textarea md-qa-input" autoFocus value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => { if (skipBlur.current) { skipBlur.current = false; return; } commit(); }}
      onKeyDown={e => {
        if (e.key === 'Escape') { cancel(); }
        else if (e.key === 'Enter') { e.preventDefault(); commit(); }
      }}
    />
  );

  return (
    <div className="md-qa">
      <span className="md-eyebrow">{label}</span>

      {items.length === 0 && editIdx !== 'new' ? (
        <p
          className={'md-text' + (editable ? ' md-editable' : '')}
          onClick={editable ? () => open('new', '') : undefined}
          title={editable ? 'Click to add' : undefined}
        ><span className="md-muted">{editable ? (emptyLabel || '— click to add') : '—'}</span></p>
      ) : (
        <ul className="md-qa-list">
          {items.map((item, idx) => (
            <li className="md-qa-item" key={idx}>
              {editIdx === idx ? editor : (
                <>
                  <span
                    className={'md-text md-qa-item-text' + (editable ? ' md-editable' : '')}
                    data-testid="insight-item"
                    onClick={editable ? () => open(idx, item) : undefined}
                    title={editable ? 'Click to edit' : undefined}
                  >{item}</span>
                  {editable && (
                    <button
                      type="button" className="md-qa-del" data-testid="insight-del"
                      aria-label={`Delete ${label} entry`} title="Delete"
                      onClick={() => remove(idx)}
                    >✕</button>
                  )}
                </>
              )}
            </li>
          ))}
          {editIdx === 'new' && <li className="md-qa-item">{editor}</li>}
        </ul>
      )}

      {editable && items.length > 0 && editIdx !== 'new' && (
        <button
          type="button" className="md-qa-add" data-testid="insight-add"
          onClick={() => open('new', '')}
        >+ add</button>
      )}
    </div>
  );
}

export default InsightList;
